import { clamp01, mean } from './utils';
import type { GroupedYamnetScores } from './ml/yamnetClassifier';
import { getAcousticConfig, type AcousticRuntimeConfig } from './acousticRuntimeConfig';

export type SpeechModeKey = 'single_speaker' | 'conversation' | 'babble' | 'shouting';

export type SpeechModeFusionInput = {
  yamnet: GroupedYamnetScores;
  loudness: number;
  noiseIntensity: number;
  turnTaking: number;
  diffuseness: number;
  speechCoherence: number;
  pausePulseScore: number;
  crestFactor: number;
  peakDensity: number;
  hasSpeech: boolean;
};

const LABELS: Record<SpeechModeKey, string> = {
  single_speaker: 'Einzelsprecher',
  conversation: 'Dialog',
  babble: 'Gemurmel',
  shouting: 'Geschrei'
};

function weighted(parts: Array<[number, number]>): number {
  const totalWeight = parts.reduce((acc, [, weight]) => acc + weight, 0);
  if (totalWeight <= 0) return 0;
  return clamp01(parts.reduce((acc, [value, weight]) => acc + value * weight, 0) / totalWeight);
}

function boost(score: number, factor: number): number {
  return clamp01(score * factor);
}

export function detectClassroomAcousticModes(
  input: SpeechModeFusionInput,
  cfg: AcousticRuntimeConfig = getAcousticConfig()
) {
  const classroomBabble =
    input.diffuseness >= cfg.babbleDiffusenessMin &&
    input.noiseIntensity >= cfg.babbleNoiseIntensityMin &&
    input.turnTaking >= cfg.babbleTurnTakingMin &&
    input.loudness >= cfg.babbleLoudnessMin &&
    input.loudness <= cfg.babbleLoudnessMax &&
    input.peakDensity <= cfg.babblePeakDensityMax;

  const classroomShouting =
    input.loudness >= cfg.shoutingLoudnessThreshold ||
    input.yamnet.shouting >= cfg.yamnetShoutingTriggerMin + 0.08 ||
    (input.crestFactor >= cfg.shoutingCrestFactorMin &&
      input.loudness >= cfg.shoutingLoudnessWithCrestMin &&
      input.peakDensity >= cfg.shoutingPeakDensityMin &&
      !classroomBabble);

  const yamnetBabble =
    input.yamnet.babble >= cfg.yamnetBabbleTriggerMin &&
    input.yamnet.babble >= input.yamnet.speech + 0.02;

  return {
    classroomShouting,
    classroomBabble,
    diffuseBabble: yamnetBabble || classroomBabble,
    loudShouting: classroomShouting || input.yamnet.shouting >= cfg.yamnetShoutingTriggerMin + 0.1
  };
}

export function computeSpeechModeScores(
  input: SpeechModeFusionInput,
  cfg: AcousticRuntimeConfig = getAcousticConfig()
) {
  if (!input.hasSpeech) {
    return {
      scoreMap: {
        single_speaker: 0,
        conversation: 0,
        babble: 0,
        shouting: 0
      },
      sortedScores: [] as Array<{ key: SpeechModeKey; label: string; score: number }>
    };
  }

  const crestBoost = clamp01(input.crestFactor / 3.5);
  const peakBoost = clamp01(input.peakDensity * 2.2);
  const modes = detectClassroomAcousticModes(input, cfg);

  const shoutingIntensity = clamp01(
    mean([
      input.yamnet.shouting * 0.35,
      input.loudness * 0.35,
      crestBoost * 0.2,
      peakBoost * 0.1
    ])
  );

  const babbleDensity = clamp01(
    mean([
      input.yamnet.babble * 0.4,
      input.noiseIntensity * 0.22,
      input.diffuseness * 0.23,
      clamp01(1 - input.speechCoherence) * 0.15
    ])
  );

  const multiVoiceLikelihood = clamp01(
    mean([
      input.diffuseness * 0.34,
      input.turnTaking * 0.28,
      input.noiseIntensity * 0.22,
      babbleDensity * 0.16
    ])
  );

  const dialogueDynamics = clamp01(mean([input.turnTaking, input.pausePulseScore, input.yamnet.conversation]));

  const singleSpeakerFocus = clamp01(1 - multiVoiceLikelihood * 0.9);

  const stableSpeech = clamp01(
    mean([
      input.yamnet.speech * 0.4 * singleSpeakerFocus,
      input.speechCoherence * 0.35 * singleSpeakerFocus,
      clamp01(1 - input.turnTaking) * 0.15,
      clamp01(1 - babbleDensity) * 0.1
    ])
  );

  let scoreMap: Record<SpeechModeKey, number> = {
    single_speaker: weighted([
      [stableSpeech, 0.4],
      [clamp01(1 - babbleDensity), 0.3],
      [clamp01(1 - shoutingIntensity), 0.2],
      [clamp01(1 - dialogueDynamics), 0.1]
    ]),
    conversation: weighted([
      [dialogueDynamics, 0.45],
      [input.yamnet.conversation, 0.25],
      [clamp01(1 - shoutingIntensity), 0.15],
      [clamp01(1 - babbleDensity), 0.15]
    ]),
    babble: weighted([
      [babbleDensity, 0.45],
      [input.yamnet.babble, 0.35],
      [clamp01(1 - stableSpeech), 0.1],
      [clamp01(1 - dialogueDynamics), 0.1]
    ]),
    shouting: weighted([
      [shoutingIntensity, 0.45],
      [input.yamnet.shouting, 0.35],
      [input.loudness, 0.12],
      [peakBoost, 0.08]
    ])
  };

  const yamnetSpeechOverBabble = input.yamnet.speech > input.yamnet.babble + 0.06;
  const yamnetSpeechOverShouting = input.yamnet.speech > input.yamnet.shouting + 0.06;

  if (modes.diffuseBabble) {
    const babbleBoost = modes.classroomBabble ? (yamnetSpeechOverBabble ? 1.58 : 1.48) : 1.18;
    const singlePenalty = modes.classroomBabble ? (yamnetSpeechOverBabble ? 0.52 : 0.58) : 0.82;
    scoreMap = {
      ...scoreMap,
      babble: boost(scoreMap.babble, babbleBoost),
      single_speaker: clamp01(scoreMap.single_speaker * singlePenalty),
      conversation: clamp01(scoreMap.conversation * 0.88)
    };
  }

  if (modes.loudShouting) {
    const shoutingFactor = modes.classroomShouting
      ? yamnetSpeechOverShouting
        ? 1.72
        : 1.62
      : 1.2;
    const singlePenalty = modes.classroomShouting
      ? yamnetSpeechOverShouting
        ? 0.42
        : 0.48
      : 0.78;
    scoreMap = {
      ...scoreMap,
      shouting: boost(scoreMap.shouting, shoutingFactor),
      single_speaker: clamp01(scoreMap.single_speaker * singlePenalty),
      conversation: clamp01(scoreMap.conversation * (modes.classroomShouting ? 0.62 : 0.88)),
      babble: clamp01(scoreMap.babble * 0.85)
    };
  }

  if (modes.classroomBabble && modes.classroomShouting) {
    if (input.crestFactor >= cfg.shoutingCrestFactorMin && input.loudness >= 0.58) {
      scoreMap = {
        ...scoreMap,
        shouting: boost(scoreMap.shouting, 1.12),
        babble: clamp01(scoreMap.babble * 0.88)
      };
    } else {
      scoreMap = {
        ...scoreMap,
        babble: boost(scoreMap.babble, 1.08),
        shouting: clamp01(scoreMap.shouting * 0.9)
      };
    }
  }

  const sortedScores = (Object.keys(scoreMap) as SpeechModeKey[])
    .map((key) => ({
      key,
      label: LABELS[key],
      score: Number(scoreMap[key].toFixed(3))
    }))
    .sort((a, b) => b.score - a.score);

  return { scoreMap, sortedScores };
}
